// "You got a thing." The overlay that says so, and asks for the click.
//
// The model is `celebrations.ts`; this is the pixels and the one interaction.
// The click is not ceremony — it is the difference between being *told* you
// won something and *taking* it, and it is the only reason this is a modal
// rather than a line in the chrome.
//
// **The plate is the kit's, and the *technique* is the celebration's.** The
// cut-corner clip-path frame is gone — every surface in this interface is a
// panel with a one-pixel edge, and a silhouette nothing else on the page wears
// is a second visual language (PLAN-UI §28.2). What came across from the old
// stylesheet is what made it read as a reward rather than as a dialog
// appearing: a slow glow growing behind it, a pop-in that *overshoots*, a
// bright bar swept across the plate, and a burst of grade-coloured ingots on
// the click. Kept, on `keyframes` from the kit with the reduced-motion guard.
//
// **The plate is shared, and this file is where it lives** — `RunOver`,
// `BranchPicker` and `ObservePicker` import it, exactly as they shared
// `Celebration.css` before. A game that announces its best moment and its
// worst in two unrelated visual languages reads as two games.
//
// Three rules it lives under, all from elsewhere in this UI:
//
//  - **Nothing here may reflow the canvas.** `disableScrollLock`: MUI's modal
//    otherwise hides the body's overflow and pads for the scrollbar, and a
//    canvas that resizes flashes black.
//  - **Everything that moves is a keyframe**, never a value in `sx`: emotion
//    mints a class per distinct value and never releases it, and this animates
//    every frame it is up.
//  - **It takes the pointer, unlike every other overlay.** `PieceOverlays` is
//    `pointerEvents: none` because the board is what a click is for; a modal
//    is the exception, and a click landing on a board you cannot see would be
//    a move you did not mean to make.

import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { GRADE_SLOTS } from "src/engine/rules/industry/state";
import { METAL } from "src/engine/rules/materials";
import type { Celebration } from "src/engine/session/celebrations";
import {
  Btn,
  keyframes,
  Modal,
  REDUCED_MOTION,
  STILL,
  styled,
  Text
} from "./kit";
import { UNIT_TONE } from "./unitTone";

/**
 * Which mood the plate is in.
 *
 * `reward` bounces and glints; `grave` settles and does neither, because what
 * it announces is not good news. One plate, two entrances — a second component
 * would be a second visual language.
 */
export type PlateTone = "reward" | "grave";

const SHARDS = 22;

/** How long the burst has the screen before the plate starts to go. */
const BURST_MS = 420;

/** The plate's exit, which `drop` has to match. */
const LEAVE_MS = 260;

/*
 * The technique, as keyframes.
 *
 * The overshoot is 0.6 → 1.06 → 1, which is the whole difference between "a
 * reward arrived" and "a dialog opened". `settle` is the same arrival with the
 * bounce taken out.
 */
const pop = keyframes`
  0%   { transform: scale(0.6) translateY(18px); opacity: 0; }
  70%  { transform: scale(1.06) translateY(0); opacity: 1; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
`;

const settle = keyframes`
  0%   { transform: scale(1.04) translateY(-10px); opacity: 0; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
`;

const drop = keyframes`
  0%   { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.86) translateY(10px); opacity: 0; }
`;

const glowGrow = keyframes`
  0%   { transform: scale(0.2); opacity: 0; }
  6%   { transform: scale(0.85); opacity: 1; }
  100% { transform: scale(1.15); opacity: 0.85; }
`;

const sweep = keyframes`
  0%        { left: -60%; }
  55%, 100% { left: 130%; }
`;

/** The token's own turn, and the two endings' arrivals. */
const spin = keyframes`
  0%, 100% { transform: rotateY(0deg) scale(1); }
  50%      { transform: rotateY(180deg) scale(1.08); }
`;

const throwOut = keyframes`
  0% {
    transform: translate(-50%, -50%) rotate(0deg) scale(0.4);
    opacity: 0;
  }
  12% { opacity: 1; }
  100% {
    transform:
      translate(calc(-50% + var(--dx)), calc(-50% + var(--dy)))
      rotate(var(--spin))
      scale(1);
    opacity: 0;
  }
`;

/**
 * The plate, and the glow behind it.
 *
 * The glow is the dialog *container's* `::before` rather than an element: it
 * has to sit between the backdrop and the paper, and the container is the only
 * box that spans both. Growing the whole time it is up, which is what gives
 * the moment a length — the plate arrives in a fifth of a second and the light
 * behind it is still opening six seconds later.
 */
export const Plate = styled(Modal, {
  shouldForwardProp: (prop: string) => prop !== "tone" && prop !== "leaving"
})<{ tone?: PlateTone; leaving?: boolean }>(({ theme, tone = "reward", leaving }) => {
  const c = theme.kit.color;
  const light = tone === "reward" ? c.brand : c.rust;
  return {
    "& .MuiDialog-container": {
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        top: "50%",
        left: "50%",
        width: "min(120vw, 120vh)",
        aspectRatio: "1",
        marginTop: "calc(-1 * min(60vw, 60vh))",
        marginLeft: "calc(-1 * min(60vw, 60vh))",
        pointerEvents: "none",
        background: `radial-gradient(circle, color-mix(in srgb, ${light} 30%, transparent) 0%, color-mix(in srgb, ${light} 11%, transparent) 34%, transparent 62%)`,
        animation: `${glowGrow} 6s ease both`,
        [REDUCED_MOTION]: STILL
      }
    },
    "& .MuiDialog-paper": {
      position: "relative",
      // **Never clipped**, so the burst can be thrown from inside the plate
      // and land outside it. The sweep does its own clipping; the body does
      // its own scrolling.
      overflow: "visible",
      textAlign: "center",
      animation: leaving
        ? `${drop} ${LEAVE_MS}ms ease both`
        : tone === "reward"
          ? `${pop} ${BURST_MS}ms ${theme.kit.motion.easeBounce} both`
          : `${settle} 460ms cubic-bezier(0.2, 0.9, 0.3, 1) both`,
      [REDUCED_MOTION]: STILL
    }
  };
});

/**
 * The bar of light crossing the plate, clipped to it.
 *
 * A flourish and not information, so it belongs to `reward` alone: a plate
 * that glints is a plate that is pleased with itself, which is the wrong note
 * for a run that has just ended.
 */
const Shine = styled("div")(({ theme }) => ({
  position: "absolute",
  inset: 0,
  overflow: "hidden",
  borderRadius: theme.kit.radius.md,
  pointerEvents: "none",
  "&::after": {
    content: '""',
    position: "absolute",
    left: "-60%",
    top: "-40%",
    width: "60%",
    height: "190%",
    // 9%, not the old sheet's 18%: that bar crossed a *gold* plate and this
    // one crosses a dark panel, where the same lift reads as a grey wedge
    // covering half the card rather than as light on metal.
    background: `linear-gradient(100deg, transparent 0%, color-mix(in srgb, ${theme.kit.color.ink} 9%, transparent) 50%, transparent 100%)`,
    transform: "rotate(18deg)",
    animation: `${sweep} 2.6s ease-in-out ${BURST_MS}ms infinite`,
    [REDUCED_MOTION]: STILL
  }
}));

/**
 * The plate's contents: one column, centred, scrolling if it has to.
 *
 * The scroll is here rather than on the paper because the paper may not clip —
 * see `Plate`.
 */
export const PlateBody = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.kit.space.sm,
  padding: `${theme.kit.space.xl}px ${theme.kit.space.xl}px ${theme.kit.space.lg}px`,
  maxHeight: "80vh",
  overflowY: "auto"
}));

/** The line naming the moment — "Front cleared", "Recovered". */
export const Kicker = styled(Text)({ letterSpacing: "0.18em", textTransform: "uppercase" });

/** The big line: what you got, or what happened. */
export const Reward = styled("div")(({ theme }) => ({
  fontFamily: theme.kit.font.heading,
  fontWeight: 400,
  fontSize: 26,
  lineHeight: 1.15,
  color: theme.kit.color.ink
}));

/**
 * The scorecard: however many numbers `statsOf` returns, in one row.
 *
 * A grid rather than a flex row, because the column positions must not move
 * when a three-digit ore count turns up — a row you have to re-read is the
 * thing this replaced. `grid-auto-flow: column` rather than a fixed count, or
 * a fifth stat wraps onto a line of its own the moment one is added.
 */
export const Stats = styled("div")(({ theme }) => ({
  display: "grid",
  gridAutoFlow: "column",
  gridAutoColumns: "minmax(0, 1fr)",
  gap: theme.kit.space.xs,
  width: "100%",
  padding: `${theme.kit.space.sm}px 0`,
  borderTop: `1px solid ${theme.kit.color.line}`,
  borderBottom: `1px solid ${theme.kit.color.line}`
}));

const StatCell = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 2,
  minWidth: 0
});

/** Prose on the plate: a reading measure, and never centred past one line. */
export const Detail = styled(Text)({ maxWidth: "34ch", lineHeight: 1.45 });

/**
 * One number and its shortest true word.
 *
 * The tone comes off `celebrations.ts` as a grade colour — it is *material*,
 * the one thing in this interface allowed a colour the tokens do not have
 * (PLAN-UI §28.2) — and it rides in `style` because it is data.
 */
export function StatRow({
  id,
  label,
  value,
  tone
}: {
  id: string;
  label: string;
  value: number;
  tone: string;
}) {
  return (
    <StatCell data-testid={`stat-${id}`}>
      <Text role="display" size={22} weight={700} style={{ color: tone }}>
        {value}
      </Text>
      <Text
        role="display"
        tone="dim"
        size={10}
        weight={700}
        sx={{ letterSpacing: "0.1em", textTransform: "uppercase" }}
      >
        {label}
      </Text>
    </StatCell>
  );
}

/**
 * The token: a shape that turns, standing in for the thing you were given.
 *
 * Loose on the board an artifact is a spinning gold octahedron and nothing
 * else is, so this is the same object arriving rather than a new icon for the
 * occasion. `shape` is a clip-path polygon and `still` is for the endings,
 * which arrive rather than turn.
 */
export const Token = styled("div", {
  shouldForwardProp: (prop: string) =>
    prop !== "shape" && prop !== "still" && prop !== "dull"
})<{ shape: string; still?: boolean; dull?: boolean }>(
  ({ theme, shape, still, dull }) => {
    const c = theme.kit.color;
    const lit = dull ? c.line : c.brand;
    return {
      width: 46,
      height: 46,
      background: dull
        ? `linear-gradient(160deg, ${c.inkDim}, ${c.line} 45%, ${c.panelRaised})`
        : `linear-gradient(160deg, ${c.cream}, ${c.brand} 45%, ${c.brandDown})`,
      clipPath: shape,
      boxShadow: `0 0 22px color-mix(in srgb, ${lit} 45%, transparent)`,
      // A fallen plate is a *position*, so the tilt is a transform rather than
      // an animation that arrives at one — it has to survive reduced motion.
      ...(still ? { transform: "rotate(-13deg)" } : { animation: `${spin} 3.4s ease-in-out infinite` }),
      [REDUCED_MOTION]: STILL
    };
  }
);

/** The four silhouettes the four plates use. */
export const SHAPES = {
  /** The artifact's own octahedron. */
  prize: "polygon(50% 0, 100% 50%, 50% 100%, 0 50%)",
  /** A stage prize is ground, not a trinket: a plate rather than a crystal. */
  ground: "polygon(14% 0, 86% 0, 100% 30%, 100% 100%, 0 100%, 0 30%)",
  /** A run is won by shipping something *off* the board, so this one leans. */
  rocket: "polygon(50% 0, 78% 38%, 78% 84%, 62% 100%, 38% 100%, 22% 84%, 22% 38%)"
} as const;

/** The burst's layer: centred on the plate, spilling well past it. */
const Burst = styled("div")({
  position: "absolute",
  inset: 0,
  pointerEvents: "none"
});

const Shard = styled("span")({
  position: "absolute",
  left: "50%",
  top: "50%",
  width: 11,
  height: 8,
  borderRadius: 1,
  animation: `${throwOut} 900ms cubic-bezier(0.16, 0.8, 0.3, 1) both`,
  [REDUCED_MOTION]: STILL
});

/**
 * The plate's shell, so the four moments do not each rebuild it.
 *
 * `open` is always true — every one of these is mounted only while it is
 * showing — and it is passed rather than assumed because MUI's `Dialog` wants
 * it, and a dialog that mounts closed is one that never animates in.
 */
export function PlateShell({
  tone = "reward",
  leaving,
  testid,
  label,
  plateTestid,
  onClose,
  children
}: {
  tone?: PlateTone;
  leaving?: boolean;
  testid: string;
  label: string;
  plateTestid: string;
  /** Backdrop and Escape. Omitted where the moment has to be answered. */
  onClose?: () => void;
  children: ReactNode;
}) {
  return (
    <Plate
      open
      tone={tone}
      leaving={leaving}
      data-testid={testid}
      aria-label={label}
      onClose={onClose}
      // The canvas must not reflow — see the header.
      disableScrollLock
      maxWidth={false}
      slotProps={{ paper: { sx: { width: "min(470px, 88vw)" } } }}
    >
      {tone === "reward" && <Shine />}
      {/* The plate's testid rides on the body rather than on MUI's paper:
          `slotProps.paper` is typed as `PaperProps` and will not carry a
          `data-` attribute without a cast, and the body is the plate as far as
          a smoke reading its text is concerned. */}
      <PlateBody data-testid={plateTestid}>{children}</PlateBody>
    </Plate>
  );
}

export function CelebrationOverlay({
  celebration,
  remaining,
  onClaim
}: {
  celebration: Celebration;
  /** How many are queued behind this one, so the button can say so. */
  remaining: number;
  onClaim(): void;
}) {
  const [leaving, setLeaving] = useState(false);
  const claim = useRef(onClaim);
  claim.current = onClaim;

  // A fresh set per celebration, and *deterministic* per id: two clients
  // watching one game should not disagree about the picture, and a fresh roll
  // on every render would re-throw the burst whenever anything re-rendered.
  const shards = useMemo(() => burst(celebration.id), [celebration.id]);
  const [claimed, setClaimed] = useState(false);

  // The exit's two timers, so they can be cancelled. A celebration is taken by
  // a click, by a backdrop click, and by three different keys, so `take` is
  // reachable several times over — and every extra call used to schedule
  // another `claim`, which shifts the queue again and swallows the prize
  // behind this one without ever showing it.
  const timers = useRef<number[]>([]);

  const take = useCallback(() => {
    // Idempotent. `timers` is the flag that is already tracking this.
    if (timers.current.length > 0) return;
    setClaimed(true);
    // **The throw gets the screen to itself before the plate goes.**
    //
    // Both used to start on the same tick, so the burst — which is the entire
    // point of making this a click rather than a banner — played behind a
    // plate that was already shrinking and fading over it. Measured at 240ms
    // after the click there was nothing left on screen at all.
    timers.current.push(window.setTimeout(() => setLeaving(true), BURST_MS));
    timers.current.push(window.setTimeout(() => claim.current(), BURST_MS + LEAVE_MS));
  }, []);

  // Cancelled on the way out, or a claim lands on whatever is mounted next.
  // The overlay is keyed by the prize, so the next celebration is a different
  // instance — and a stray timer from this one would take it as well.
  useEffect(
    () => () => {
      for (const timer of timers.current) window.clearTimeout(timer);
      timers.current = [];
    },
    []
  );

  // Enter and Escape both take it. A modal you can only dismiss with the mouse
  // is one that traps a keyboard, and there is nothing here to decline — the
  // reward is already yours; the click is for the watching of it.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " " || event.key === "Escape") {
        event.preventDefault();
        take();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [take]);

  const stage = celebration.kind === "stage";

  return (
    <PlateShell
      testid="celebration"
      plateTestid="celebration-plate"
      label={`${celebration.title}: ${celebration.reward}`}
      leaving={leaving}
      // Clicking the backdrop takes it too. Anything else is a modal that
      // punishes you for aiming badly at your own prize.
      onClose={take}
    >
      <Token shape={stage ? SHAPES.ground : SHAPES.prize} />

      <Kicker role="display" tone="brand" size={12} weight={700}>
        {stage ? "Front cleared" : "Recovered"}
      </Kicker>
      {!stage && (
        <Text tone="dim" size={15} weight={700}>
          {celebration.title}
        </Text>
      )}
      <Reward data-testid="celebration-reward">{celebration.reward}</Reward>

      {/* **The numbers, not the prose.** A stage card is a scorecard: what
          the front cost you and what came out of it. The one line of text
          under it says what the new ground *is*, which is the only part of
          the old copy that changed from stage to stage. */}
      {celebration.kind === "stage" && (
        <Stats data-testid="celebration-stats">
          {celebration.stats.map((stat) => (
            <StatRow key={stat.id} {...stat} />
          ))}
        </Stats>
      )}

      {/* A stage card's line is a *list*, not prose, so it takes the plate's
          full width rather than the reading measure — which broke it after
          "1 promotion" and left "square" on a line of its own. */}
      <Detail tone="dim" size={13} sx={stage ? { maxWidth: "none" } : undefined}>
        {celebration.detail}
      </Detail>

      <Btn
        variant="primary"
        data-testid="celebration-claim"
        onClick={take}
        autoFocus
        sx={{ mt: 0.5 }}
      >
        {stage ? "Press on" : "Take it"}
      </Btn>
      {remaining > 0 && (
        <Text tone="dim" size={11}>
          {remaining} more waiting
        </Text>
      )}

      {/* Thrown from the plate's centre and landing well outside it, which is
          why the paper may not clip — see `Plate`. */}
      {claimed && (
        <Burst>
          {shards.map((shard, index) => (
            <Shard
              key={index}
              // In `style`, never `sx` — see the header. These are twenty-two
              // distinct values that exist for under a second.
              style={{
                background: shard.colour,
                ["--dx" as string]: `${shard.dx}px`,
                ["--dy" as string]: `${shard.dy}px`,
                ["--spin" as string]: `${shard.spin}deg`,
                animationDelay: `${shard.delay}ms`
              }}
            />
          ))}
        </Burst>
      )}
    </PlateShell>
  );
}

interface Shard {
  colour: string;
  dx: number;
  dy: number;
  spin: number;
  delay: number;
}

/**
 * A burst thrown outward, seeded on the celebration's id.
 *
 * **Ingots in the metal ladder's own colours**, off `UNIT_TONE` rather than a
 * table of hexes here: that is what this factory makes, and a generic confetti
 * shape in a colour the board never uses would be the one thing on screen that
 * means nothing. Material is the one exception to the no-hex rule (PLAN-UI
 * §28.2) and this is it, spelled the way `unitRows.tsx` spells it.
 *
 * Seeded rather than random for the same reason everything else here is: two
 * clients drawing one game have to draw it the same, and a fresh roll per
 * render would re-throw the whole burst every time React looked at it.
 */
function burst(seed: string): Shard[] {
  const grades = [0, 1, 2, 3].map((grade) => UNIT_TONE(METAL * GRADE_SLOTS + grade));
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    hash = Math.imul(hash ^ seed.charCodeAt(i), 16777619) >>> 0;
  }
  const next = (): number => {
    hash = Math.imul(hash ^ (hash >>> 15), 2246822507) >>> 0;
    return (hash >>> 8) / 0xffffff;
  };

  return Array.from({ length: SHARDS }, (_, index) => {
    // Spread around the circle with a little jitter, so it reads as thrown
    // rather than as a wheel.
    const angle = (index / SHARDS) * Math.PI * 2 + (next() - 0.5) * 0.5;
    const reach = 110 + next() * 170;
    return {
      colour: grades[Math.floor(next() * grades.length)],
      dx: Math.round(Math.cos(angle) * reach),
      // Biased upward: material thrown up and falling reads better than a
      // symmetric ring, which looks like an explosion diagram.
      dy: Math.round(Math.sin(angle) * reach - 40),
      spin: Math.round((next() - 0.5) * 720),
      delay: Math.round(next() * 90)
    };
  });
}
